// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppConfig } from "../../config/config";
import { httpStatus, SnackMessage } from "../../config/constant";
import { APIService } from "../../utils/apiService";
import {
  GetExamsRequestState,
  RegisteredExamsPayload,
} from "../../utils/types";
import { enqueueSnackbarMessage } from "../commonSlice/common";

const initialState: GetExamsRequestState = {
  state: "idle",
  stateMessage: null,
  errorMessage: null,
  response: [],
  backgroundProcess: false,
  backgroundProcessMessage: null,
};

export const getExamDetails = createAsyncThunk(
  "exams/getExamDetails",
  async (id: string, { dispatch }) => {
    return new Promise<{ exam: RegisteredExamsPayload[] }>(
      (resolve, reject) => {
        APIService.getInstance()
          .get(AppConfig.serviceUrls.getExam + "?id=" + id)
          .then((resp) => {
            if (resp.status == 200) {
              resolve({ exam: resp.data });
            } else {
              reject("Something went wrong");
            }
          })
          .catch((error: Error) => {
            dispatch(
              enqueueSnackbarMessage({
                message: SnackMessage.error.examDetails,
                type: "error",
              })
            );
            reject(error);
          });
      }
    );
  }
);

const selectedExamSlice = createSlice({
  name: "selectedExam",
  initialState,
  reducers: {
    setSelectedExam: (state, action: PayloadAction<RegisteredExamsPayload>) => {
      state.response = [action.payload];
      state.state = "success";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExamDetails.pending, (state, action) => {
        state.stateMessage = "Loading Exams";
        state.state = "loading";
        state.backgroundProcess = true;
        state.backgroundProcessMessage = "Fetching Exam Data";
      })
      .addCase(getExamDetails.fulfilled, (state, action) => {
        state.response = action.payload.exam;
        state.state = "success";
        state.backgroundProcess = false;
      })
      .addCase(getExamDetails.rejected, (state, action) => {
        state.state = "failed";
        state.backgroundProcess = false;
      });
  },
});

export const { setSelectedExam } = selectedExamSlice.actions;
export default selectedExamSlice.reducer;
